// @need-translate

import {useMutation} from '@tanstack/react-query';
import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {storeMutationAdapter} from '../../../../adapter/storeMutationAdapter';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {CommentStatus} from '../../../../common/shared/api/nicolas/vo/enums/CommentStatus.enum';
import {twMerge} from '../../../../common/twMerge';
import useTypedSelector from '../../../../common/useTypedSelector';
import {Button} from '../../../../design-system/atoms/Button/Button';
import {
  ButtonColorScheme,
  ButtonSize,
} from '../../../../design-system/atoms/Button/Button.enum';
import {Divider} from '../../../../design-system/atoms/Divider/Divider';
import {Icon} from '../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {CommentInputField} from '../../../../design-system/organisms/CommentInputField/CommentInputField';
import {CommentItem} from '../../../../design-system/organisms/CommentItem/CommentItem';
import {CommentSpoilerButton} from '../../../../design-system/organisms/CommentSpoilerButton/CommentSpoilerButton';
import {EmoticonPreview} from '../../../../design-system/organisms/EmoticonPreview/EmoticonPreview';
import {MOBILE_MAIN_TAB_HEIGHT_PX} from '../../../../design-system/organisms/MainTab/constants';
import {PcCommentInputField} from '../../../../design-system/organisms/PcCommentInputField';
import {PC_HEADER_HEIGHT} from '../../../../design-system/organisms/PcHeader/constants';
import {commonErrorHandler} from '../../../../errors/commonErrorHandler';
import {PagewebErrorType} from '../../../../errors/PagewebErrorType.enum';
import {
  CommentItem as CommentItemType,
  Maybe,
  ResultStatus,
} from '../../../../graphql/generated/types';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../header/constants';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {theme} from '../../../../theme';
import {showToast} from '../../../../toast/toastHandler';
import {useLogin} from '../../../../user/useLogin.hook';
import {
  useBlockUserListQuery,
  useCommentItemQuery,
  useInfiniteReplyCommentListQuery,
} from '../../../../wrapper';
import {CommentMoreMenuContainer} from '../../container/CommentMoreMenuContainer';
import {useSetCommentQueryData} from '../../hook/useSetCommentQueryData';
import {useUpdateCommentEmoticon} from '../../hook/useUpdateCommentEmoticon';
import {useWriteCommentBlockDialog} from '../../hook/useWriteCommentBlockDialog';
import {EmoticonItem} from '../../type';
import {getCommentCooldownLocalStorage} from '../../util/getCommentCooldownLocalStorage';
import {setCommentCooldownLocalStorage} from '../../util/setCommentCooldownLocalStorage';
import {setSelectEmoticonCallback} from '../../util/setSelectEmoticonCallback';

export interface CommentItemContainerProps {
  seriesId?: number;
  productId?: number;
  sortType?: Maybe<string>;
  item: CommentItemType;
  // reply Comment인 경우에만 유효합니다.
  parentUid?: number;
  isOnlyComment?: boolean;
  isProductComment?: boolean;
  isContentHome?: boolean;
}

export function CommentItemContainer({
  seriesId,
  productId,
  sortType,
  item,
  parentUid,
  isOnlyComment,
  isProductComment,
  isContentHome,
}: CommentItemContainerProps) {
  const isReplyComment = !!parentUid;
  const user = useTypedSelector(state => state.user.user);
  const userUid = useTypedSelector(state => state.user.user?.uid || '');
  const {isLogin, doLogin} = useLogin();
  const checkLogin = useCallback(() => {
    if (isLogin) {
      return undefined;
    }
    doLogin();
  }, [doLogin, isLogin]);
  const blockUserList =
    useBlockUserListQuery(undefined, {enabled: isLogin}).data?.blockUserList
      ?.list || [];
  const isMyComment = Number(user?.uid) === item.userUid;
  const [shouldShowReply, setShouldShowReply] = useState(!!isOnlyComment);

  const onClickReply = useCallback(
    (e: MouseEvent) => {
      aggregateAndSendLog({
        targetElem: e.target as HTMLElement,
        eventMeta: shouldShowReply
          ? undefined
          : {
              id: String(item.commentUid),
              type: 'commentId',
              series_id: String(item.seriesId || ''),
            },
        actionType: 'Event',
        actionName: shouldShowReply ? '답글닫기_클릭' : '댓글달기_클릭',
        customProps: {
          user_action: 'click',
        },
      });
      setShouldShowReply(!shouldShowReply);
    },
    [item.commentUid, item.seriesId, shouldShowReply]
  );

  const commentRef = useRef<HTMLDivElement>(null);
  const {isMobile} = requestContext.requestInfo.display;
  const closeReply = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT('답글닫기_클릭', e, () => {
        setShouldShowReply(false);
        if (commentRef.current) {
          const commentTop =
            window.pageYOffset + commentRef.current.getBoundingClientRect().top;
          window.scrollTo(
            0,
            commentTop -
              (isMobile
                ? MOBILE_HEADER_HEIGHT_PX + MOBILE_MAIN_TAB_HEIGHT_PX
                : PC_HEADER_HEIGHT)
          );
        }
      });
    },
    [isMobile]
  );

  const {refetch: refetchCommentItem} = useCommentItemQuery(
    {
      parentUid: item.commentUid,
      seriesId,
    },
    {
      enabled: !isReplyComment && !!isOnlyComment,
    }
  );

  const {
    isLoading: isLoadingReply,
    data: replyCommentData,
    refetch: refetchReplyComment,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteReplyCommentListQuery(
    {
      commentListInput: {
        page: 0,
        parentUid: item.commentUid,
        seriesId: item.seriesId,
        productId: item.productId,
      },
    },
    {
      enabled: !isReplyComment && shouldShowReply,
      initialPageParam: {
        commentListInput: {
          page: 0,
          parentUid: item.commentUid,
          seriesId: item.seriesId,
          productId: item.productId,
        },
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.replyCommentList?.isEnd
          ? undefined
          : {
              commentListInput: {
                page: pages.length,
                parentUid: item.commentUid,
                seriesId: item.seriesId,
                productId: item.productId,
              },
            };
      },
    }
  );

  const [commentInputText, setCommentInputText] = useState('');
  const [selectedEmoticon, setSelectedEmoticon] = useState<
    EmoticonItem | undefined
  >();
  const onChangeText = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setCommentInputText(e.target.value);
  }, []);
  // 모댓글 삭제시 입력 필드를 초기화 해줍니다.
  useEffect(() => {
    if (
      item.commentStatus === CommentStatus.Deleted ||
      item.commentStatus === CommentStatus.DeletedByMe
    ) {
      setCommentInputText('');
    }
  }, [item.commentStatus]);

  const {setCommentQueryData} = useSetCommentQueryData({
    seriesId,
    productId,
    commentUid: isOnlyComment ? item.commentUid : undefined,
    parentUid,
    sortType,
  });
  const {mutateAsync: mutateLikeComment} = useMutation(
    storeMutationAdapter.likeComment()
  );
  const onClickLike = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT('좋아요_클릭', e, async () => {
        if (!isLogin) {
          doLogin();
          return;
        }
        if (isMyComment) {
          showToast({
            message: t('comment_toast_like_impossible'),
            isMobile,
            delayMs: 2000,
          });
          return;
        }
        const result = await mutateLikeComment({
          likeCommentInput: {
            like: !item.liked,
            commentUid: item.commentUid,
          },
        });
        if (result.likeComment.status === ResultStatus.Success) {
          await setCommentQueryData({
            newCommentData: {
              commentUid: item.commentUid,
              likeCount: result.likeComment.likeCount,
              liked: !item.liked,
            },
          });
        }
      });
    },
    [
      doLogin,
      isLogin,
      isMobile,
      isMyComment,
      item.commentUid,
      item.liked,
      mutateLikeComment,
      setCommentQueryData,
    ]
  );

  const [isActiveMore, setIsActiveMore] = useState(false);

  const onClickMore = useCallback(
    (e: MouseEvent) => {
      LOG_CLICK_EVENT(isMyComment ? '삭제_클릭' : '더보기_클릭', e, () => {
        setIsActiveMore(true);
      });
    },
    [isMyComment]
  );

  const deactiveMore = useCallback(() => {
    setIsActiveMore(false);
  }, []);

  const {showBlockDialog, blockDialogComponent} = useWriteCommentBlockDialog();

  const [isSpoilerInputChecked, setIsSpoilerInputChecked] = useState(false);
  const onClickSpoiler = useCallback(() => {
    setIsSpoilerInputChecked(!isSpoilerInputChecked);
  }, [isSpoilerInputChecked]);

  const {mutateAsync: mutateAsyncWriteComment} = useMutation(
    storeMutationAdapter.writeComment()
  );

  const onSubmit = useCallback(async () => {
    if (commentInputText || selectedEmoticon) {
      const {uid, expires} = getCommentCooldownLocalStorage();
      if (uid === userUid && new Date(expires) > new Date()) {
        commonErrorHandler({
          isSerializedPagewebError: true,
          type: PagewebErrorType.CommentCooldownError,
          message: t('comment_toast_overload'),
        });
        return;
      }

      setCommentCooldownLocalStorage(userUid);

      const result = await mutateAsyncWriteComment({
        writeCommentInput: {
          seriesId: item.seriesId,
          productId: item.productId,
          isSpoiler: isSpoilerInputChecked,
          comment: selectedEmoticon
            ? `(이모티콘)${commentInputText}`
            : commentInputText,
          parentUid: item.commentUid,
          emoticon: selectedEmoticon
            ? JSON.stringify({
                item_id: selectedEmoticon.item_id,
                item_sub_type: selectedEmoticon.item_sub_type,
                item_ver: selectedEmoticon.item_version,
                resource_id: selectedEmoticon.resource_id,
              })
            : undefined,
        },
      });
      setCommentInputText('');
      setSelectedEmoticon(undefined);
      setIsSpoilerInputChecked(false);

      if (result.writeComment.status === ResultStatus.Success) {
        // 특정 코멘트 하나만 있는 상세페이지인 경우 댓글도 새로고침해서 답글 갯수를 헤더에 다시 반영할 수 있도록 합니다.
        if (isOnlyComment) {
          await refetchCommentItem();
        }
        await refetchReplyComment();
      } else {
        showBlockDialog(result.writeComment.blockMsg);
      }
    }
  }, [
    commentInputText,
    selectedEmoticon,
    userUid,
    mutateAsyncWriteComment,
    item.seriesId,
    item.productId,
    item.commentUid,
    isSpoilerInputChecked,
    isOnlyComment,
    refetchReplyComment,
    refetchCommentItem,
    showBlockDialog,
  ]);

  const emoticonKeyboardWrapperRef = useRef<HTMLDivElement>(null);

  const onClickEmoticonIcon = useCallback((e: MouseEvent) => {
    LOG_CLICK_EVENT('이모티콘버튼_클릭', e, async () => {
      await setSelectEmoticonCallback(emoticon => {
        setSelectedEmoticon(emoticon);
      });
      window.Kakao.Emoticon.toggleKeyboard(emoticonKeyboardWrapperRef.current);
    });
  }, []);

  useUpdateCommentEmoticon({commentList: replyCommentData});

  const clearEmoticon = useCallback(() => {
    setSelectedEmoticon(undefined);
  }, []);

  return (
    <div
      ref={commentRef}
      {...putTiaraObjectProps({
        customProps: {
          ...(item.productId ? {event_episode_id: String(item.productId)} : {}),
        },
      })}
    >
      <div
        className={
          isReplyComment && !isOnlyComment
            ? 'bg-theme-bg-surface-alt'
            : undefined
        }
        {...putTiaraObjectProps({
          customProps: {
            event_comment_id: String(item.commentUid),
          },
        })}
      >
        <CommentItem
          item={{
            ...item,
            commentStatus:
              item.userUid && blockUserList.includes(item.userUid)
                ? CommentStatus.BlockedByUser
                : (item.commentStatus as CommentStatus),
          }}
          isReply={isReplyComment}
          isMyComment={isMyComment}
          onClickMore={onClickMore}
          onClickLike={onClickLike}
          onClickReply={onClickReply}
          onClickSpoilerVisible={e => LOG_CLICK_EVENT('스포일러보기_클릭', e)}
          shouldShowReply={shouldShowReply}
        />
      </div>
      {!isReplyComment && shouldShowReply && (
        <>
          {isLoadingReply && (
            <div className="flex-center-full">
              <Loading />
            </div>
          )}
          {replyCommentData?.pages.map(page =>
            page.replyCommentList?.commentList.map(replyItem => (
              <CommentItemContainer
                item={replyItem}
                key={replyItem.commentUid}
                seriesId={replyItem.seriesId ?? undefined}
                productId={replyItem.productId ?? undefined}
                parentUid={item.commentUid}
                isOnlyComment={isOnlyComment}
                isContentHome={isContentHome}
              />
            ))
          )}
          {selectedEmoticon && (
            <EmoticonPreview
              imageUrl={selectedEmoticon.image_url}
              onClickClose={clearEmoticon}
            />
          )}
          <div
            className={twMerge(
              'pl-79pxr',
              !isOnlyComment && 'bg-theme-bg-surface-alt'
            )}
            onClick={checkLogin}
          >
            {isMobile && (
              <>
                <CommentInputField
                  key={item.commentStatus}
                  value={commentInputText}
                  onChange={onChangeText}
                  onSubmit={user ? onSubmit : undefined}
                  readOnly={
                    !user ||
                    item.commentStatus === CommentStatus.Deleted ||
                    item.commentStatus === CommentStatus.DeletedByMe
                  }
                  onClickEmoticonIcon={onClickEmoticonIcon}
                  isEmoticonSelected={!!selectedEmoticon}
                  placeholder={
                    item.commentStatus === CommentStatus.Deleted ||
                    item.commentStatus === CommentStatus.DeletedByMe
                      ? t('comment_toast_deleted_comment')
                      : t('comment_reply_input')
                  }
                  isMobile={isMobile}
                  onClick={e =>
                    LOG_CLICK_EVENT('답글입력필드_클릭', e, checkLogin)
                  }
                  onClickSend={e => {
                    LOG_CLICK_EVENT('전송버튼_클릭', e, () => {
                      if (user) {
                        onSubmit();
                      }
                    });
                  }}
                />
                <div
                  className={twMerge([
                    'pb-15pxr',
                    !isOnlyComment && 'bg-theme-bg-surface-alt',
                  ])}
                >
                  <CommentSpoilerButton
                    {...putTiaraObjectProps({
                      click: {
                        layer1: '스포일러',
                        copy: isSpoilerInputChecked ? 'OFF' : 'ON',
                      },
                    })}
                    isSpoiler={isSpoilerInputChecked}
                    onClickSpoiler={e =>
                      LOG_CLICK_EVENT('스포일러옵션_클릭', e, onClickSpoiler)
                    }
                  />
                </div>
                <div
                  ref={emoticonKeyboardWrapperRef}
                  // inline style none을 넣어둬야 emoticon keyboard toggle 했을 때 바로 이모티콘 키보드가 열립니다.
                  style={{display: 'none'}}
                  className="mt-10pxr"
                />
              </>
            )}
            {!isMobile && (
              <>
                <PcCommentInputField
                  inputBoxClassName="py-10pxr"
                  value={commentInputText}
                  onChange={onChangeText}
                  readOnly={
                    !user ||
                    item.commentStatus === CommentStatus.Deleted ||
                    item.commentStatus === CommentStatus.DeletedByMe
                  }
                  placeholder={
                    item.commentStatus === CommentStatus.Deleted ||
                    item.commentStatus === CommentStatus.DeletedByMe
                      ? t('comment_toast_deleted_comment')
                      : t('comment_reply_input')
                  }
                  isMobile={isMobile}
                  onClick={e => LOG_CLICK_EVENT('답글입력필드_클릭', e)}
                  onClickSend={e => {
                    LOG_CLICK_EVENT('전송버튼_클릭', e, () => {
                      if (user) {
                        onSubmit();
                      }
                    });
                  }}
                />
                <div
                  className={twMerge([
                    'pb-15pxr pt-10pxr',
                    !isOnlyComment && 'bg-theme-bg-surface-alt',
                  ])}
                >
                  <CommentSpoilerButton
                    {...putTiaraObjectProps({
                      click: {
                        layer1: '스포일러',
                        copy: isSpoilerInputChecked ? 'OFF' : 'ON',
                      },
                    })}
                    isSpoiler={isSpoilerInputChecked}
                    onClickSpoiler={e =>
                      LOG_CLICK_EVENT('스포일러옵션_클릭', e, onClickSpoiler)
                    }
                  />
                </div>
              </>
            )}
          </div>
          <div
            className={twMerge(
              'flex items-center justify-end px-18pxr pb-16pxr pt-6pxr',
              !isOnlyComment && 'bg-theme-bg-surface-alt'
            )}
          >
            {hasNextPage && (
              <div
                className="flex w-full cursor-pointer items-center justify-center pl-40pxr"
                onClick={e =>
                  LOG_CLICK_EVENT('답글더보기_클릭', e, () => fetchNextPage())
                }
              >
                <Text className="font-small1 text-theme-solid-100">
                  답글 더 보기
                </Text>
                <Icon type={IconType.TabArrowDown} className="ml-4pxr" />
              </div>
            )}
            <Button
              size={ButtonSize.Small}
              isRound
              colorScheme={ButtonColorScheme['4st']}
              onClick={closeReply}
            >
              {t('common_close_button')}
            </Button>
          </div>
          {!isMobile && isContentHome && (
            <div className="pl-94pxr">
              <Divider
                color={theme.colors['theme-transparent-10']}
                sizePx={1}
                direction="horizontal"
                className="w-full"
              />
            </div>
          )}
        </>
      )}
      {blockDialogComponent}
      <CommentMoreMenuContainer
        seriesId={seriesId}
        productId={productId}
        sortType={sortType}
        isActiveMoreMenu={isActiveMore}
        deactiveMoreMenu={deactiveMore}
        isMyComment={isMyComment}
        commentItem={item}
        parentUid={parentUid}
        isProductComment={isProductComment}
        isOnlyComment={isOnlyComment}
      />
    </div>
  );
}
